import {
  AnimatedCard,
  AnimatedCardProps,
} from "@/shared/base/components/AnimatedCard/AnimatedCard";
import {
  InventoryType,
  SeatLevelMultiInventory,
} from "@/global/store/data/types";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { AdaListItem, InventoryTag } from "./inventoryCard.helpers";
import { inventoryTypeUIDs } from "@/global/config/constants";
import React from "react";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { createSeatLevelCardTitle } from "../utils.chooseSeats";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

interface Props {
  cardVariant?: AnimatedCardProps["variant"];
  domId?: string;
  domRef?: any;
  inventory: InventoryType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
  hideViewSeatButton?: boolean;
  onClickViewSeat?: (section: string) => void;
}

export function SeatLevelMultiInventoryCard(props: Props) {
  const inventory = props.inventory as SeatLevelMultiInventory;
  const teamConfig = useTeamConfig();

  // Use pricingInformation from inventory API response
  const pricingInfo = inventory.pricingInformation || [];

  // Calculate individual prices for each seat type from API data
  const calculateCategorizedPricing = () => {
    let totalPrice = 0;
    const breakdown: {
      title: string;
      count: number;
      pricePerSeat: number;
      total: number;
    }[] = [];

    // Filter out zero seat counts and sort by orderIndex
    const validPricing = pricingInfo
      .filter((item: any) => item.seatCount > 0)
      .sort((a: any, b: any) => a.orderIndex - b.orderIndex);

    validPricing.forEach((pricingItem: any) => {
      const pricePerSeat = Number(
        pricingItem.allInPrice || pricingItem.basePrice
      );
      const total = pricePerSeat * pricingItem.seatCount;
      totalPrice += total;

      breakdown.push({
        title: pricingItem.pricingLabel.replace(":", ""), // Remove colon from "Adult Price:"
        count: pricingItem.seatCount,
        pricePerSeat,
        total,
      });
    });

    return { totalPrice, breakdown };
  };

  const { totalPrice, breakdown } = calculateCategorizedPricing();
  const totalSelectedSeats = pricingInfo.reduce(
    (total: number, pricing: any) => total + pricing.seatCount,
    0
  );

  // * values to render
  const SEAT_TITLE = createSeatLevelCardTitle(inventory, teamConfig);
  const { sm: smDown, md: mdDown } = useCssMediaQuery("down");

  return (
    <AnimatedCard
      variant={props?.cardVariant}
      style={props?.style}
      onRender={props?.onRender}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      borderRadius={"10px"}
      footerHeight={!props.hideViewSeatButton ? "95px" : "70px"}
      isSelected={props?.isSelected}
      isDisabled={props?.isDisabled}
      isDisabledHoverAnim={props?.isDisabledHoverAnim}
      isFooterHidden={props?.isFooterHidden}
      renderBody={() => {
        return (
          <Box onClick={props.onSelect}>
            <Stack
              sx={{
                flexDirection: "row",
                position: "absolute",
                top: 0,
                right: "8px",
                zIndex: 1,
                transform: "translate(0%, -50%) scale(1)",
                overflow: "hidden",
                gap: 1,
              }}
            >
              {inventory?.containsAisle ? (
                <InventoryTag value={"Aisle Seat"} />
              ) : null}

              {inventory.priceCodeTag ? (
                <InventoryTag value={inventory.priceCodeTag} />
              ) : null}

              {inventory.sectionNameTag ? (
                <InventoryTag value={inventory.sectionNameTag} />
              ) : null}
            </Stack>

            <Box p={2} py={!mdDown ? 2.2 : 1.5}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  lineHeight={1.1}
                  dangerouslySetInnerHTML={{ __html: SEAT_TITLE }}
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                ></Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                >
                  {getUSCurrency(totalPrice)}
                </Typography>
              </Stack>
            </Box>
            <Divider />

            <Stack padding={"1rem"} py={!mdDown ? 2.2 : 1.5}>
              {inventory.inventoryType === inventoryTypeUIDs.ada && props ? (
                <AdaListItem
                  name="Seat Type"
                  value={inventory?.seatType as string}
                  variant={
                    inventory?.seatType == "WHEELCHAIR" ? "blue" : "orange"
                  }
                />
              ) : null}

              {/* Categorized pricing breakdown */}
              {breakdown.map((item, index) => (
                <Box
                  key={index}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  gap={"0.5rem"}
                  mb={0.5}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontSize={smDown ? "0.7rem" : "0.85rem"}
                    fontWeight={"500"}
                  >
                    {item.title} ({item.count}x{" "}
                    {getUSCurrency(item.pricePerSeat)}):
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontSize={smDown ? "0.7rem" : "0.85rem"}
                    fontWeight={"500"}
                  >
                    {getUSCurrency(item.total)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 1 }} />

              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={"0.5rem"}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"600"}
                >
                  Total ({totalSelectedSeats} seats):
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"600"}
                >
                  {getUSCurrency(totalPrice)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Stack width={"100%"} p={2} gap={0.7}>
            <Button
              variant={props.isSelected ? "outlined" : "contained"}
              onClick={props.onSelect}
              sx={{ width: "100%" }}
              disabled={totalSelectedSeats === 0}
            >
              {props.isSelected ? "Unselect" : "Select +"}
            </Button>

            {!props.hideViewSeatButton ? (
              <Button
                variant="outlined"
                sx={{ width: "100%" }}
                onClick={() => {
                  const section =
                    inventory?.mapSectionName || inventory?.sectionName;
                  props?.onClickViewSeat?.(section);
                }}
              >
                View Seat
              </Button>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}
